import React, { useState } from 'react';
import { X } from 'lucide-react';
import Rating from './Rating';

const ReviewModal = ({ showModal, setShowModal, review, setReview, editingReview, onSave }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!review.title.trim()) {
      newErrors.title = 'Movie title is required';
    }

    if (!review.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    } else if (review.excerpt.length < 10) {
      newErrors.excerpt = 'Excerpt must be at least 10 characters';
    } else if (review.excerpt.length > 200) {
      newErrors.excerpt = 'Excerpt must be less than 200 characters';
    }

    if (!review.content.trim()) {
      newErrors.content = 'Review content is required';
    } else if (review.content.length < 50) {
      newErrors.content = 'Review content must be at least 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Keep the form busy until the save really finishes
      await onSave(review);
      setErrors({});
    } catch (err) {
      setErrors({ general: 'Failed to save review. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setReview({ ...review, [field]: value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        className="modal-panel max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={handleClose} className="icon-btn absolute right-4 top-4" disabled={isLoading} aria-label="Close">
          <X size={18} strokeWidth={1.75} />
        </button>

        <h2 id="review-modal-title" className="text-xl font-semibold text-slate-50">
          {editingReview ? 'Edit Review' : 'Add New Review'}
        </h2>
        <p className="mt-2 text-sm text-slate-400">
          {editingReview ? 'Update your movie review' : 'Share your thoughts about this movie'}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6" noValidate>
          <div>
            <label htmlFor="review-title" className="field-label">Movie Title</label>
            <input
              id="review-title"
              type="text"
              value={review.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter the movie title..."
              required
              disabled={isLoading}
              aria-invalid={Boolean(errors.title)}
              className={`field ${errors.title ? 'field-invalid' : ''}`}
            />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="review-rating" className="field-label">
              Rating: <span className="font-mono">{review.rating}/10</span>
            </label>
            <input
              id="review-rating"
              type="range"
              min="1"
              max="10"
              value={review.rating}
              onChange={(e) => handleInputChange('rating', parseInt(e.target.value))}
              disabled={isLoading}
              className="w-full cursor-pointer accent-sky-400"
            />
            <div className="mt-3">
              <Rating value={review.rating} />
            </div>
          </div>

          <div>
            <label htmlFor="review-excerpt" className="field-label">
              Excerpt <span className="font-mono font-normal text-slate-400">({review.excerpt.length}/200)</span>
            </label>
            <textarea
              id="review-excerpt"
              value={review.excerpt}
              onChange={(e) => handleInputChange('excerpt', e.target.value)}
              placeholder="Write a brief, compelling excerpt (1-2 sentences)..."
              rows="2"
              maxLength="200"
              required
              disabled={isLoading}
              aria-invalid={Boolean(errors.excerpt)}
              className={`field-area ${errors.excerpt ? 'field-invalid' : ''}`}
            />
            {errors.excerpt && <p className="field-error">{errors.excerpt}</p>}
          </div>

          <div>
            <label htmlFor="review-content" className="field-label">
              Review Content <span className="font-mono font-normal text-slate-400">({review.content.length} characters)</span>
            </label>
            <textarea
              id="review-content"
              value={review.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Write your detailed movie review here. Share your thoughts on the story, acting, cinematography, and overall experience..."
              rows="8"
              required
              disabled={isLoading}
              aria-invalid={Boolean(errors.content)}
              className={`field-area ${errors.content ? 'field-invalid' : ''}`}
            />
            {errors.content && <p className="field-error">{errors.content}</p>}
          </div>

          {errors.general && <p role="alert" className="form-alert">{errors.general}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} disabled={isLoading} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary flex-1">
              {isLoading
                ? (editingReview ? 'Updating...' : 'Publishing...')
                : (editingReview ? 'Update Review' : 'Publish Review')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;